import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Alert, AlertDescription, AlertTitle, Button, Card, CardContent, CardHeader, CardTitle } from '@bookaburra/ui';
import { CheckCircle2 } from 'lucide-react';
import { fetchBookingTenant } from '../booking-flow/api';
import { useDocumentTitle } from '../lib/use-document-title';

interface BookedSearch {
  /** Plaintext magic-link token minted alongside the booking. Lets us
   *  link straight to /m/<token> from the confirmation page so the
   *  customer can manage / cancel without hunting through email.
   *  Absent on direct-URL hits — in that case we hide the button
   *  rather than render a broken link. */
  manageToken?: string;
}

export const Route = createFileRoute('/book/$slug/booked/$bookingId')({
  validateSearch: (search: Record<string, unknown>): BookedSearch => ({
    manageToken:
      typeof search.manageToken === 'string' && search.manageToken.length > 0
        ? search.manageToken
        : undefined,
  }),
  component: BookedConfirmation,
});

function BookedConfirmation() {
  const { slug, bookingId } = Route.useParams();
  const { manageToken } = Route.useSearch();
  const tenantQ = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  useDocumentTitle(
    tenantQ.data ? `Booking confirmed · ${tenantQ.data.tenant.name}` : 'Booking confirmed',
  );

  return (
    <main className="mx-auto max-w-2xl px-6 py-16 sm:px-12">
      <Alert variant="success">
        <CheckCircle2 className="h-4 w-4" />
        <AlertTitle>Booking confirmed</AlertTitle>
        <AlertDescription>
          We&rsquo;ve scheduled your appointment. You&rsquo;ll receive a confirmation
          email shortly.
        </AlertDescription>
      </Alert>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Booking reference</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="font-mono text-sm text-muted-foreground">{bookingId}</p>
          <p className="mt-3 text-sm text-muted-foreground">
            Keep this reference handy. If you need to change or cancel, get in
            touch with the team.
          </p>
        </CardContent>
      </Card>

      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        {manageToken ? (
          <a href={`/m/${manageToken}`}>
            <Button>Make changes to this booking</Button>
          </a>
        ) : null}
        <Link to="/book/$slug" params={{ slug }}>
          <Button variant="outline">Book another service</Button>
        </Link>
      </div>
    </main>
  );
}
